<script setup>
import MarketGroupCard from '@/components/Market/MarketGroupCard.vue';
import MatchStats from '@/components/Match/MatchStats.vue';
import { useFixturesStore } from '@/stores/fixtures';
import { useWidgetStore } from '@/stores/widget'
import { useBetslipStore } from '@/stores/betslip';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref , watch, onBeforeUnmount} from 'vue';
import { useRoute } from 'vue-router';
import { socket  } from '@/feed.socket';
import _ from 'lodash';

const fixtures = useFixturesStore()
const widget = useWidgetStore()
const betslips = useBetslipStore()
const { marketGroups } = storeToRefs(fixtures)
const route = useRoute()
const eid = computed(() => route.params.id)
const event = ref({})
const { selections } = storeToRefs(fixtures)
const { isEventSelected } = storeToRefs(betslips)

const activeGroup = ref(null)

const accessToken = ref('')
const iframeUrl = ref('')
const locale = localStorage.getItem('lang') 
const widgetUrl = computed(() => import.meta.env.VITE_WIDGET_URL)


onMounted(async () => {
  await fixtures.fetchGroups()
  event.value = fixtures.getEvent(eid.value)
  if (!event.value) return
  socket.emit('fixture-odds', {i: eid.value})
 // const token = await widget.getValidWidgetToken()
  //accessToken.value = token

  const sportName = event.value.sn?.toLowerCase().replace(/\s+/g, '_') || 'soccer'
  const eventId = route.params.id
  //iframeUrl.value = `${widgetUrl.value}/${sportName}/${locale}/${eventId}?token=${token}`

  await fixtures.fetchEventSelection(eid.value, event.value?.s)
})

const groupsOfEvent = computed(() => {
  if (!selections.value.length || !marketGroups.value.length) return []

  const eventSel = selections.value.find(s => Number(s.fi) === Number(eid.value))
  if (!eventSel || !Array.isArray(eventSel.m)) return []

  const eventMarketIds = new Set(eventSel.m.map(m => Number(m.i)))

  return marketGroups.value
    .filter(group => group.ml.some(marketId => eventMarketIds.has(Number(marketId))))
    .sort((a, b) => (a.s ?? 9999) - (b.s ?? 9999))
})

watch(groupsOfEvent, (groups) => {
  if (!activeGroup.value && groups.length) {
    activeGroup.value = groups[0].i
  }
})

const eventMarketsByActiveGroup = computed(() => {
  if (!activeGroup.value || !selections.value.length) return []

  const eventSel = selections.value.find(s => Number(s.fi) === Number(eid.value))
  if (!eventSel || !Array.isArray(eventSel.m)) return []

  const group = groupsOfEvent.value.find(g => g.i === activeGroup.value)
  if (!group) return []

  return eventSel.m
    .filter(market => group.ml.includes(Number(market.i)))
    .map(market => {
      const fullMarket = fixtures.allMarkets.find(am => am.i === Number(market.i))
      return {
        ...market,
        sortOrder: fullMarket?.s ?? 9999,
        nrsValue: Array.isArray(market.nrs) ? Number(market.nrs[0]) : 9999
      }
    })
    .sort((a, b) => {
      // sort with s
      const sort = a.sortOrder - b.sortOrder
      if (sort !== 0) return sort

      // sort with nrs
      return a.nrsValue - b.nrsValue
    })
})

onBeforeUnmount(async () => {
    if (!isEventSelected.value(event.value.i)) {
        socket.emit('leave-fixture-odds', {i: event.value.i})  
    }
})
</script>
<template>
    <div class="flex flex-col-reverse xl:flex-row overflow-hidden" v-if="event">
        <div class="overflow-y-auto flex-1 scroll-fix">
            <div class="elv-EventHeader hidden xl:block pb-4 relative">
                <div class="px-8">
                    <div class="pt-4">
                        <RouterLink
                            :to="`/in-play?s=${event.si}`"
                            class="text-white text-xs"
                        >
                            &lt; {{$t('app.10')}} {{$te(`sports.${event.si}`) ? $t(`sports.${event.si}`) :  event.sn}}  - {{ event.cn }}
                        </RouterLink>
                    </div>
                    <div class="py-6">
                        <h2 class="text-white text-xl font-bold">{{ event.h }} v {{ event.a }}</h2>
                    </div>

                    <div>
                        <ul class="list-none flex space-x-4">
                            <li v-for="group in groupsOfEvent" :key="group.i">
                                <div
                                    class="text-[13px] text-white cursor-pointer px-2 py-1"
                                    :class="{'bg-[var(--color-primary3)] rounded-2xl !text-black font-bold': group.i == activeGroup}"
                                    @click="activeGroup = group.i"
                                >
                                    {{ $te(`marketsGroups.${group.n}`) ?  $t(`marketsGroups.${group.n}`) :  group.n}}
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <!-- <div class="iframe-container" v-if="accessToken">
                <iframe
                    :src="iframeUrl"
                    style="width: 100%; max-width: 480px; min-height: 470px; border: none;"
                    allowfullscreen
                ></iframe>
            </div> -->
            <div>
                <ul class="xl:hidden px-2 py-3 list-none flex space-x-4 border-t border-b border-[var(--color-primary6)] overflow-x-auto">
                    <li v-for="group in groupsOfEvent">
                        <div
                            class="text-[13px] text-white cursor-pointer px-2 py-1"
                            :class="{'bg-[var(--color-primary3)] rounded-2xl !text-black font-bold': group.i == activeGroup}"
                            @click="activeGroup = group.i"
                        >
                            {{ $te(`marketsGroups.${group.n}`) ?  $t(`marketsGroups.${group.n}`) :  group.n}}
                        </div>
                    </li>
                </ul>
            </div>

            <div class="py-1.5 xl:py-4 px-2 xl:px-6 bg-[var(--color-grey3)] relative" v-if="activeGroup">
                <template v-for="market in eventMarketsByActiveGroup" :key="market.key"> 
                    <MarketGroupCard
                        :market="market"
                        :odds="(market?.o || []).slice().sort((a,b) => (a.s ?? 9999) - (b.s ?? 9999))"
                        :event="event"
                    />
                </template>
            </div>
        </div>
        <MatchStats :event="event" />
    </div>
</template>

<style scoped>
.elv-EventHeader::before {
    content: "";
    position: absolute;
    width: 100%;
    background-size: 100% 100%;
    background-image: linear-gradient(rgba(12, 22, 20, .1), transparent 20px), radial-gradient(122% 370px at center -220px, #009969 0, transparent 100%), linear-gradient(to right bottom, #0c1614, #084436);
    height: var(--elv-header-height);
    z-index: -1;
}
.iframe-container{
    border: 1px solid #556071;
    padding: 5px;
    display: flex;
    flex-direction: column;
    transition: all .5s;
}
.scroll-fix {
  -webkit-overflow-scrolling: touch;
  will-change: transform;
  transform: translateZ(0);
  backface-visibility: hidden;
  position: relative;
}
iframe {
  transform: translateZ(0);
  backface-visibility: hidden;
  will-change: opacity, transform;
}
</style>